// @flow
import _ from 'lodash';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
// Components
import { Link } from 'react-router-dom';
import { CountdownBar } from '../../common';
import CartChangeNotice from '../components/CartChangeNotice';
import CartIndicatorButton from '../components/CartIndicatorButton';
// Composers
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
// Selectors
import { getBlends } from '../../blend/actions';
import { blendsByOrderNumberSelector } from '../../blend/selectors';
import {
  allItemsSelector,
  gettingOrderLoadingSelector,
  itemCountSelector,
  itemTotalSumSelector,
  orderNumberSelector,
} from '../selectors';
// Actions
import { deleteItem, findOrCreateOrder } from '../actions';
// Analytics
import * as Analytics from '../../common/analytics';
// Helpers
import { computeCartChanges } from '../utils/computeCartChanges';
import { toSimpleLineItem } from '../utils/toSimpleLineItem';
// Styles
import './CartIndicator.css';

// Small delays so the cart preview doesn't flicker when the mouse just passes over the cart
const HOVER_OPEN_DELAY = 150;
const HOVER_CLOSE_DELAY = 250;
const CHANGE_NOTICE_DURATION = 10000;

type Props = {
  itemCount: number,
  orderNumber: ?string,
  loadingOrder: boolean,
  subtotal: number,
  findOrCreateOrder: () => void,
};

type State = {
  cartChanges: {}[],
  noticeVisible: boolean, // Whether we should display the cart change notice
};

const initialState = {
  cartChanges: [],
  noticeVisible: false,
};

const CartIndicator = (props: Props) => {
  const timerRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const prevLineItemsRef = useRef(props.lineItems);
  const location = useLocation();

  const [cartChanges, setCartChanges] = useState(initialState.cartChanges);
  const [noticeVisible, setNoticeVisible] = useState(
    initialState.noticeVisible,
  );
  // Whether the cart preview was opened by hovering the cart
  const [hoverOpen, setHoverOpen] = useState(false);
  const [countdownKey, setCountdownKey] = useState(0);
  const [countdownPaused, setCountdownPaused] = useState(false);

  const isDesktop = window.innerWidth > 768;
  const isCartPage = props.location?.pathname?.includes('/carrinho');
  const blends = props.blends(props.orderNumber);

  const cartItems = useMemo(
    () => props.lineItems.map(toSimpleLineItem),
    [props.lineItems],
  );

  // Current state of the changed items, so the notice follows quantity changes made from it
  const changedCartItems = useMemo(() => {
    const changedIds = new Set(cartChanges.map(item => item.id));
    return cartItems.filter(item => changedIds.has(item.id));
  }, [cartChanges, cartItems]);

  useEffect(() => {
    props.findOrCreateOrder();
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Navigating (ex: clicking "Ver carrinho") unmounts the preview under the mouse, so mouseleave never fires
  useEffect(() => {
    clearTimeout(hoverTimerRef.current);
    setHoverOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const prevLineItems = prevLineItemsRef.current;
    prevLineItemsRef.current = props.lineItems;

    // If the order was just loaded (ex: Data from server just returned from GET) we should not check for changes
    if (props.orderNumber === null) {
      return;
    }

    // If we navigate to the cart page the notice should disappear
    if (location.pathname === '/carrinho/') {
      setNoticeVisible(false);

      return;
    }

    // This protects against the notice being removed due to the final action of an optimistic update.
    if (_.isEqual(prevLineItems, props.lineItems)) {
      return;
    }

    // Changes made from the open preview are already visible in it
    if (hoverOpen) {
      return;
    }

    const changedLineItems = computeCartChanges(prevLineItems, props.lineItems);

    if (changedLineItems.length !== 0) {
      setCartChanges(changedLineItems);
      setNoticeVisible(true);
      setCountdownKey(key => key + 1);
      setCountdownPaused(false);

      const hasMix = changedLineItems.some(l => l.mixId);
      const orderNumber = props.orderNumber;
      if (hasMix && orderNumber) {
        props.getBlends(orderNumber);
      }

      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setCartChanges([]);
        setNoticeVisible(false);
      }, CHANGE_NOTICE_DURATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderNumber, props.lineItems, location.pathname]);

  // Blends are only fetched when a mix changes, so the cart preview may need to load them
  const loadMissingBlends = () => {
    const loadedBlendIds = new Set(blends.map(blend => blend.id));
    const hasMissingBlend = props.lineItems.some(
      lineItem => lineItem.mixId && !loadedBlendIds.has(lineItem.mixId),
    );

    if (hasMissingBlend && props.orderNumber) {
      props.getBlends(props.orderNumber);
    }
  };

  const handleMouseEnter = () => {
    clearTimeout(hoverTimerRef.current);
    // Keep the change notice open while the user is looking at it
    clearTimeout(timerRef.current);
    setCountdownPaused(true);

    if (!isDesktop || isCartPage || props.itemCount <= 0 || hoverOpen) {
      return;
    }

    hoverTimerRef.current = setTimeout(() => {
      loadMissingBlends();
      setHoverOpen(true);
    }, HOVER_OPEN_DELAY);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimerRef.current);

    if (!hoverOpen && !noticeVisible) {
      return;
    }

    hoverTimerRef.current = setTimeout(() => {
      setHoverOpen(false);
      setNoticeVisible(false);
    }, HOVER_CLOSE_DELAY);
  };

  const handleDismiss = () => {
    clearTimeout(hoverTimerRef.current);
    setHoverOpen(false);
    setNoticeVisible(false);
  };

  const handleRemoveItem = (lineItemId: number) => {
    const lineItem = _.find(props.lineItems, item => item.id === lineItemId);
    Analytics.logLineItemRemove(
      lineItem,
      window.location.pathname,
      'cart preview',
    );
    return props.removeItem(lineItemId);
  };

  // Only show the notice if we are not in the cart page and there was a recent item change or the cart is hovered
  const showNotice = (noticeVisible || hoverOpen) && isDesktop && !isCartPage;

  return (
    <div
      className="cart-indicator-main"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link to="/carrinho/" className="cart-indicator-link">
        <CartIndicatorButton
          quantity={props.itemCount}
          loading={props.loadingOrder}
        />
      </Link>

      {showNotice && (
        <CartChangeNotice
          variant={noticeVisible ? 'added' : 'cart'}
          cartChanges={noticeVisible ? changedCartItems : cartItems}
          blends={blends}
          itemCount={props.itemCount}
          subtotal={props.subtotal}
          onDismiss={handleDismiss}
          onRemoveItem={handleRemoveItem}
          countdown={
            noticeVisible && (
              <CountdownBar
                key={countdownKey}
                duration={CHANGE_NOTICE_DURATION}
                paused={countdownPaused}
              />
            )
          }
        />
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    itemCount: itemCountSelector(state),
    lineItems: allItemsSelector(state),
    orderNumber: orderNumberSelector(state),
    loadingOrder: gettingOrderLoadingSelector(state),
    subtotal: itemTotalSumSelector(state),
    blends: orderNumber => blendsByOrderNumberSelector(state, orderNumber),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps) => {
  return {
    findOrCreateOrder() {
      dispatch(findOrCreateOrder());
    },
    getBlends(orderNumber: string) {
      dispatch(getBlends(orderNumber));
    },
    removeItem(lineItemId: number) {
      return dispatch(deleteItem(lineItemId));
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export default withRouter(connector(CartIndicator));
